import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { InsightsCard } from "./InsightsCard";
import { NodeOutlineIcon } from "./InsightsIcons";
import { INSIGHTS_COLORS } from "./colors";
// Geometry of the design (viewBox 240): two 12px rings with a 7px gap.
const SIZE = 240;
const STROKE = 12;
const GAP = 7;
const OUTER_R = SIZE / 2 - STROKE / 2;
const INNER_R = OUTER_R - STROKE - GAP;
/**
 * One ring: the full circle in the light (inactive) colour, the active share drawn over it as a rounded arc from
 * 12 o'clock clockwise.
 */
const Ring = ({ radius, active, inactive, track, arc, onHover }) => {
    const total = active + inactive;
    const circumference = 2 * Math.PI * radius;
    const share = total > 0 ? active / total : 0;
    const c = SIZE / 2;
    return (
    // Hovering the ring (its stroke) shows this ring's total in the centre.
    React.createElement("g", { style: { cursor: "pointer" }, onMouseEnter: onHover },
        React.createElement("circle", { cx: c, cy: c, r: radius, fill: "none", stroke: track, strokeWidth: STROKE }),
        share >= 1 ? (React.createElement("circle", { cx: c, cy: c, r: radius, fill: "none", stroke: arc, strokeWidth: STROKE })) : share > 0 ? (React.createElement("circle", { cx: c, cy: c, r: radius, fill: "none", stroke: arc, strokeWidth: STROKE, strokeLinecap: "round", strokeDasharray: `${share * circumference} ${circumference}`, transform: `rotate(-90 ${c} ${c})` })) : null));
};
/**
 * Card 1: access points (outer ring, blue) and nodes (inner ring, amber); the strong arc is the active share. The
 * total is in the centre and the four counts below.
 */
export const DeviceStatusCard = ({ data, loading, error }) => {
    const { t } = useTranslation(["common"]);
    const d = data !== null && data !== void 0 ? data : { activeAccessPoints: 0, inactiveAccessPoints: 0, activeNodes: 0, inactiveNodes: 0 };
    const total = d.activeAccessPoints + d.inactiveAccessPoints + d.activeNodes + d.inactiveNodes;
    const activeAps = t("common:catalog.insights.activeAps", "Active APs");
    const inactiveAps = t("common:catalog.insights.inactiveAps", "Inactive APs");
    const activeNodes = t("common:catalog.insights.activeNodes", "Active Nodes");
    const inactiveNodes = t("common:catalog.insights.inactiveNodes", "Inactive Nodes");
    const legend = [
        { label: activeAps, value: d.activeAccessPoints, color: INSIGHTS_COLORS.apActive },
        { label: inactiveAps, value: d.inactiveAccessPoints, color: INSIGHTS_COLORS.apInactive },
        { label: activeNodes, value: d.activeNodes, color: INSIGHTS_COLORS.nodeActive },
        { label: inactiveNodes, value: d.inactiveNodes, color: INSIGHTS_COLORS.nodeInactive },
    ];
    const n = (value) => value.toLocaleString();
    // Centre: the hovered ring's total; nodes by default and again once the pointer leaves the rings.
    const [focus, setFocus] = useState("node");
    const centre = focus === "ap"
        ? { label: t("common:catalog.insights.totalAps", "Total\nAPs"), value: d.activeAccessPoints + d.inactiveAccessPoints }
        : { label: t("common:catalog.insights.totalNodes", "Total\nNodes"), value: d.activeNodes + d.inactiveNodes };
    return (React.createElement(InsightsCard, { title: t("common:catalog.insights.devices", "Devices"), icon: React.createElement(NodeOutlineIcon, { size: 26 }), info: t("common:catalog.insights.devicesInfo", "Access points and nodes, active and inactive."), loading: loading, error: error, height: 320, emptyText: !loading && !error && total === 0 ? t("common:catalog.insights.noDevices", "No devices yet.") : null },
        React.createElement(Box, { sx: { display: "flex", flexDirection: "column", alignItems: "center", height: "100%" } },
            React.createElement(Box, { sx: { position: "relative", height: 240, maxWidth: "100%", aspectRatio: "1 / 1" } },
                React.createElement("svg", { width: "100%", height: "100%", viewBox: `0 0 ${SIZE} ${SIZE}`, role: "img", "aria-label": `${centre.label.replace("\n", " ")} ${centre.value}`, onMouseLeave: () => setFocus("node") },
                    React.createElement("defs", null,
                        React.createElement("linearGradient", { id: "insights-ap-arc", x1: "0", y1: "0", x2: "0", y2: "1" },
                            React.createElement("stop", { offset: "0%", stopColor: "#3B90E4" }),
                            React.createElement("stop", { offset: "100%", stopColor: "#2864D3" }))),
                    React.createElement(Ring, { radius: OUTER_R, active: d.activeAccessPoints, inactive: d.inactiveAccessPoints, track: INSIGHTS_COLORS.apInactive, arc: "url(#insights-ap-arc)", onHover: () => setFocus("ap") }),
                    React.createElement(Ring, { radius: INNER_R, active: d.activeNodes, inactive: d.inactiveNodes, track: INSIGHTS_COLORS.nodeInactive, arc: INSIGHTS_COLORS.nodeActive, onHover: () => setFocus("node") })),
                React.createElement(Box, { sx: {
                        position: "absolute",
                        inset: 0,
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                        justifyContent: "center",
                        pointerEvents: "none",
                    } },
                    React.createElement(Typography, { sx: { fontSize: 13, color: "text.secondary", lineHeight: 1.6, textAlign: "center", whiteSpace: "pre-line" } }, centre.label),
                    React.createElement(Typography, { sx: { fontSize: 30, fontWeight: 400, color: "text.primary", lineHeight: 1.4, mt: 0.5 } }, n(centre.value)))),
            React.createElement(Box, { sx: {
                    display: "grid",
                    gridTemplateColumns: "auto auto",
                    columnGap: 3,
                    rowGap: 0.75,
                    mt: 2.5,
                    justifyContent: "center",
                } }, legend.map((item) => (React.createElement(Box, { key: item.label, sx: { display: "flex", alignItems: "center", gap: 1, minWidth: 0 } },
                React.createElement(Box, { sx: { width: 10, height: 10, borderRadius: "50%", bgcolor: item.color, flexShrink: 0 } }),
                React.createElement(Typography, { noWrap: true, sx: { fontSize: 12, color: "text.secondary" } },
                    item.label,
                    " (",
                    n(item.value),
                    ")"))))))));
};
